import React from "react";
import { ArrowRight, Feather, Flame, Target } from "lucide-react";
import { CoverThumb, coverForBookId } from "./BookCover";
import { RL } from "./ReadLink";
import { plural } from "../../lib/plural";

/* One row per book inside GraphPrintz; old-system installs fold into the same row. */
function LegacyNote({ b, ro }) {
  const l = b.legacy;
  if (b.legacy_only) {
    return l.earlier_days > 0 ? (
      <div className="mc-legacy-note" data-testid={`mc-legacy-note-${b.book_id}`}>
        <span>Old system · {l.earlier_days} of {l.target} days earlier (before the 30-day window)</span>
      </div>
    ) : null;
  }
  return (
    <div className="mc-legacy-note" data-testid={`mc-legacy-note-${b.book_id}`}>
      <span>Also practised earlier (old system){l.earlier_days > 0 ? ` · ${l.earlier_days} of ${l.target} days earlier` : ""}</span>
      {!l.has_new_journey && (
        <RL ro={ro} to={l.continue_href} className="mc-legacy-note-link" data-testid={`mc-legacy-note-continue-${b.book_id}`}>
          Continue in the new journey <ArrowRight size={11} />
        </RL>
      )}
    </div>
  );
}

function BookRow({ b, ro }) {
  const pct = Math.min(100, Math.round((b.days_kept / b.target) * 100));
  const testBase = b.legacy_only ? `mc-legacy-${b.book_id}` : `mc-real-${b.book_id}`;
  const kind = b.legacy_only ? "legacy" : "real";
  return (
    <article className="mc-active-card" data-testid={testBase}>
      <header className="mc-active-head">
        <CoverThumb src={coverForBookId(b.book_id)} title={b.book_title} testId={`mc-${kind}-cover-${b.book_id}`} />
        <div className="mc-active-head-text">
          <p className="mc-active-eyebrow">{b.legacy_only ? "Legacy install" : <>CraftPrint<span className="mc-tm">™</span></>}</p>
          <h3 className="mc-active-title">{b.book_title}</h3>
          <p className="mc-active-sutra">
            {b.legacy_only ? "Practised in the old system" : `${b.idea_count} idea${b.idea_count === 1 ? "" : "s"} in daily practice`}
          </p>
        </div>
      </header>
      <div className="mc-active-progress">
        <div className="mc-active-progress-head">
          <span className="mc-active-progress-label">Days kept</span>
          <span className="mc-active-progress-value" data-testid={`mc-${kind}-days-${b.book_id}`}>{b.days_kept} of {b.target} days</span>
        </div>
        <div className="mc-active-progress-track" aria-hidden="true">
          <div className="mc-active-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        {!b.legacy_only && (
          <p className="mc-streak" data-testid={`mc-real-streak-${b.book_id}`}><Flame size={12} /> best streak {plural(b.best_streak, "day")}</p>
        )}
        {b.legacy && <LegacyNote b={b} ro={ro} />}
      </div>
      <footer className="mc-active-foot">
        {b.legacy_only ? (
          <RL ro={ro} to={b.legacy.continue_href} className="mc-btn-primary" data-testid={`mc-legacy-continue-${b.book_id}`}>
            Legacy install · Continue in the new journey <ArrowRight size={13} />
          </RL>
        ) : (
          <>
            <RL ro={ro} to={`/sadhana?book=${b.book_id}&bridge=1`} className="mc-btn-primary" data-testid={`mc-real-open-sadhana-${b.book_id}`}>
              <Feather size={13} /> Open daily practice
            </RL>
            <RL ro={ro} to={`/siddhi?book=${b.book_id}&bridge=1`} className="mc-btn-ghost" data-testid={`mc-real-open-siddhi-${b.book_id}`}>
              <Target size={13} /> See retrospective
            </RL>
          </>
        )}
      </footer>
    </article>
  );
}

export function BookPracticeRows({ rows, ro }) {
  if (!rows?.length) return null;
  return (
    <div className="gpd-card gpd-span gpd-books" data-testid="gpd-your-practices">
      <p className="gpd-label">Your practices</p>
      <p className="gpd-meaning">One CraftPrint<span className="mc-tm">™</span> per book — the three ideas you committed to and practise daily.</p>
      <div className="mc-active-grid" data-testid="mc-real-craftprint-grid">
        {rows.map((b) => <BookRow key={b.book_id} b={b} ro={ro} />)}
      </div>
    </div>
  );
}
